import { wFetch } from '../util/fetch-util'
import { ModuleRateVotesResponse, RatingMode } from './rate'
import { UserData } from './user'

export interface ArticleData {
  pageId: string
  title?: string
  source?: string
  tags?: string[]
  authors?: UserData[]
  parent?: string
  locked?: boolean
}

export interface ArticleUpdateRequest extends ArticleData {
  authorsIds?: number[]
  forcePageId?: boolean
}

export async function createArticle(data: ArticleData) {
  await wFetch(`/pw-api/articles/new`, { method: 'POST', sendJson: true, body: data })
}

export interface FullArticleRating {
  value: number
  mode: RatingMode
  votes: number
  popularity: number
}

export interface FullArticleData {
  uid: number
  pageId: string
  title: string
  canonicalUrl: string
  createdAt: string
  updatedAt: string
  createdBy: UserData
  authors: UserData[]
  updatedBy: UserData
  rating: FullArticleRating
  tags: string[]
}

export function fetchAllArticles(): Promise<FullArticleData[]> {
  return wFetch<FullArticleData[]>('/pw-api/articles')
}

export function fetchArticle(pageId: string): Promise<ArticleData> {
  return wFetch<ArticleData>(`/pw-api/articles/${pageId}`)
}

export async function updateArticle(pageId: string, data: ArticleUpdateRequest): Promise<ArticleData> {
  return wFetch<ArticleData>(`/pw-api/articles/${pageId}`, { method: 'PUT', sendJson: true, body: data })
}

export interface ArticleFormField {
  name: string
  type: string
  label: string
  hint: string
  default: string
  value: string
  options: Array<{ key: string; label: string }>
  height: number
  minLength: number
  maxLength: number
}

export interface ArticleForm {
  fields: ArticleFormField[]
  values: Record<string, string>
  broken: boolean
}

export function fetchArticleForm(pageId: string): Promise<ArticleForm> {
  return wFetch<ArticleForm>(`/pw-api/articles/${pageId}/form`)
}

export async function buildArticleFormSource(pageId: string, values: Record<string, string>): Promise<string> {
  const resp = await wFetch<{ source: string }>(`/pw-api/articles/${pageId}/form`, {
    method: 'POST',
    sendJson: true,
    body: { values },
  })
  return resp.source
}

export async function deleteArticle(pageId: string) {
  await wFetch(`/pw-api/articles/${pageId}`, { method: 'DELETE', sendJson: true })
}

export interface ArticleLogEntry {
  revNumber: number
  user: UserData
  comment: string
  defaultComment: string
  createdAt: string
  type: string
  meta: Record<string, any>
}

export interface ArticleLog {
  count: number
  entries: Array<ArticleLogEntry>
}

export async function fetchArticleLog(pageId: string, from: number = 0, to: number = from + 25): Promise<ArticleLog> {
  return await wFetch<ArticleLog>(`/pw-api/articles/${pageId}/log?from=${from}&to=${to}`)
}

export async function revertArticleRevision(pageId: string, revNumber: number): Promise<ArticleData> {
  return await wFetch(`/pw-api/articles/${pageId}/log`, { method: 'PUT', sendJson: true, body: { revNumber: revNumber } })
}

export interface ArticleVersion {
  source: string
  rendered: string
}

export async function fetchArticleVersion(pageId: string, revNum: number, pathParams?: { [key: string]: string }): Promise<ArticleVersion> {
  const urlPathParams = pathParams && `&pathParams=${JSON.stringify(pathParams ?? {})}`
  return await wFetch<ArticleVersion>(`/pw-api/articles/${pageId}/version?revNum=${revNum}${urlPathParams}`)
}

export interface ArticleBacklink {
  id: string
  title: string
  exists: boolean
}

export interface ArticleBacklinks {
  children: Array<ArticleBacklink>
  includes: Array<ArticleBacklink>
  links: Array<ArticleBacklink>
}

export async function fetchArticleBacklinks(pageId: string): Promise<ArticleBacklinks> {
  return await wFetch<ArticleBacklinks>(`/pw-api/articles/${pageId}/links`)
}

export async function fetchArticleVotes(pageId: string): Promise<ModuleRateVotesResponse> {
  return await wFetch<ModuleRateVotesResponse>(`/pw-api/articles/${pageId}/votes`)
}

export async function deleteArticleVotes(pageId: string): Promise<ModuleRateVotesResponse> {
  return await wFetch<ModuleRateVotesResponse>(`/pw-api/articles/${pageId}/votes`, { method: 'DELETE' })
}
